/**
 * personalization-burst.js — Anything AF
 *
 * Places the starburst callout over the main product image on personalized
 * products where the buyer can upload their own photo.
 *
 * Why this is a theme element and not part of the photo: Google Merchant Center
 * rejects promotional or instructional text printed onto a product image, and
 * this store's feed has been under review. Theme markup never reaches the
 * product feed, so the shopper still sees the note and Google still gets a
 * clean garment photo.
 *
 * Loaded only on products tagged `custom-image` (see
 * snippets/product-media-gallery-content-styles.liquid), so there is no tag
 * check here.
 *
 * Notes on the two things that broke during development:
 *   - The theme replaces the whole <media-gallery> element when the shopper
 *     changes variant, so the observer watches document.body. An observer bound
 *     to the gallery dies with it.
 *   - The debounce uses setTimeout, not requestAnimationFrame: rAF is throttled
 *     to a standstill in a background tab, so a variant change made before the
 *     shopper switched tabs would never repaint the burst.
 *
 * apply() is idempotent, so an observer callback with nothing to do makes no
 * mutation and cannot loop. Nothing here affects layout: the burst is
 * absolutely positioned.
 */
(function () {
  'use strict';

  if (window.__aafPersonalizationBurst) return;
  window.__aafPersonalizationBurst = true;

  var FLAG = 'customImageBurst';
  var SELECTOR = '[data-custom-image-burst]';
  var LEAD = 'If you don’t want an image, just delete it in the editor & we’ll arrange your text so it looks good!';
  var SUB = 'We always send you a mockup for approval!';

  function build() {
    var burst = document.createElement('div');
    burst.className = 'custom-image-burst';
    burst.setAttribute('role', 'note');
    burst.dataset[FLAG] = 'true';

    var body = document.createElement('span');
    body.className = 'custom-image-burst__body';

    var lead = document.createElement('span');
    lead.className = 'custom-image-burst__lead';
    lead.textContent = LEAD;

    var sub = document.createElement('span');
    sub.className = 'custom-image-burst__sub';
    sub.textContent = SUB;

    body.appendChild(lead);
    body.appendChild(sub);
    burst.appendChild(body);
    return burst;
  }

  /**
   * The first grid item and the first carousel slide are the same picture —
   * whichever one the shopper is actually looking at, and it follows the
   * selected variant. The zoom dialog and thumbnail strip are excluded.
   */
  function targets() {
    var found = [];
    var gallery = document.querySelector('media-gallery');
    if (!gallery) return found;

    var grid = gallery.querySelector('.media-gallery__grid > li:first-child .product-media');
    if (grid && !grid.closest('.dialog-zoomed-gallery')) found.push(grid);

    var slides = gallery.querySelectorAll('slideshow-slide');
    for (var i = 0; i < slides.length; i++) {
      if (slides[i].closest('.dialog-zoomed-gallery')) continue;
      var media = slides[i].querySelector('.product-media');
      if (media) found.push(media);
      break;
    }

    return found;
  }

  function apply() {
    var hosts = targets();

    var existing = document.querySelectorAll(SELECTOR);
    for (var e = 0; e < existing.length; e++) {
      var parent = existing[e].parentNode;
      if (parent && hosts.indexOf(parent) !== -1) continue;
      if (parent) {
        if (parent.classList) parent.classList.remove('custom-burst-host');
        parent.removeChild(existing[e]);
      }
    }

    for (var i = 0; i < hosts.length; i++) {
      if (hosts[i].querySelector(SELECTOR)) continue;
      hosts[i].classList.add('custom-burst-host');
      hosts[i].appendChild(build());
    }
  }

  var pending = null;

  function schedule() {
    if (pending) return;
    pending = window.setTimeout(function () {
      pending = null;
      apply();
    }, 60);
  }

  function start() {
    apply();

    // Cheap retries cover the gallery hydrating after first paint.
    window.setTimeout(apply, 250);
    window.setTimeout(apply, 1200);
    window.addEventListener('load', schedule);
    window.addEventListener('pageshow', schedule);
    document.addEventListener('visibilitychange', schedule);

    if (typeof MutationObserver !== 'function') return;

    new MutationObserver(schedule).observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
